// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { listMyCollections, createCollection, updateCollection, deleteCollection, listCollectionItems, addItemFile, removeItem } from '@/services/collections';
import { Plus, Trash2, Upload, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

export default function CollectionsManager() {
  const [collections, setCollections] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newVisibility, setNewVisibility] = useState<'public' | 'friends' | 'private'>('public');

  const refreshCollections = async () => {
    try {
      const data = await listMyCollections();
      setCollections(data);
      if (data.length && !activeId) setActiveId(data[0].id);
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error', description: e.message || 'Failed to load collections', variant: 'destructive' });
    }
  };

  const refreshItems = async (id: string) => {
    try {
      const data = await listCollectionItems(id);
      setItems(data);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to load items', variant: 'destructive' });
    }
  };

  useEffect(() => { refreshCollections(); }, []);
  useEffect(() => { if (activeId) refreshItems(activeId); }, [activeId]);

  const onCreate = async () => {
    if (!newTitle.trim()) { toast({ title: 'Title required' }); return; }
    setCreating(true);
    try {
      const id = await createCollection({ title: newTitle.trim(), visibility: newVisibility });
      setNewTitle('');
      await refreshCollections();
      setActiveId(id);
      toast({ title: 'Collection created' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to create', variant: 'destructive' });
    } finally { setCreating(false); }
  };

  const onUpload = () => fileInputRef.current?.click();
  const onFile = async (file: File) => {
    if (!activeId) return;
    setUploading(true);
    try {
      await addItemFile(activeId, file);
      await refreshItems(activeId);
      toast({ title: 'Item added' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to upload', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const onDeleteItem = async (id: string) => {
    try {
      await removeItem(id);
      if (activeId) await refreshItems(activeId);
      toast({ title: 'Item removed' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to remove', variant: 'destructive' });
    }
  };

  const onUpdateVisibility = async (value: 'public' | 'friends' | 'private') => {
    if (!activeId) return;
    try {
      await updateCollection(activeId, { visibility: value } as any);
      toast({ title: 'Visibility updated' });
      await refreshCollections();
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to update', variant: 'destructive' });
    }
  };

  const onDeleteCollection = async () => {
    if (!activeId) return;
    try {
      await deleteCollection(activeId);
      setActiveId(null);
      await refreshCollections();
      setItems([]);
      toast({ title: 'Collection deleted' });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to delete', variant: 'destructive' });
    }
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-white">Your Collections</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2">
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New collection title" className="bg-white/5 border-white/10 text-white placeholder:text-white/50" />
            <Select value={newVisibility} onValueChange={(v) => setNewVisibility(v as any)}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Visibility" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="friends">Friends</SelectItem>
                <SelectItem value="private">Private</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={onCreate} disabled={creating} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400">
              {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Create
            </Button>
          </div>
          {activeId && (
            <div className="flex items-center gap-2">
              <Button onClick={onUpload} disabled={uploading} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400"><Upload className="h-4 w-4 mr-1" /> Add Item</Button>
              <input type="file" accept="image/*,video/mp4,.heic" ref={fileInputRef} onChange={(e) => e.target.files && e.target.files[0] && onFile(e.target.files[0])} className="hidden" />
              <Button variant="outline" className="border-red-400/50 text-red-300 hover:bg-red-500/10" onClick={onDeleteCollection}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
            </div>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="md:w-64 border border-white/10 rounded-xl p-2 h-[50vh] bg-white/5">
            <ScrollArea className="h-full">
              <div className="space-y-1">
                {collections.map(c => (
                  <button key={c.id} className={`w-full text-left px-3 py-2 rounded-lg ${activeId === c.id ? 'bg-emerald-500/10 border border-emerald-400/30' : 'hover:bg-white/5 border border-transparent'} text-white`} onClick={() => setActiveId(c.id)}>
                    <div className="text-sm text-white">{c.title}</div>
                    <div className="text-xs text-white/60">{c.visibility}</div>
                  </button>
                ))}
                {collections.length === 0 && (
                  <div className="text-sm text-white/60 px-2 py-4">No collections yet.</div>
                )}
              </div>
            </ScrollArea>
          </div>
          <div className="flex-1 border border-white/10 rounded-xl p-2 h-[50vh] bg-white/5">
            {activeId ? (
              <ScrollArea className="h-full">
                <div className="flex justify-between items-center px-2 pb-2">
                  <div className="text-sm text-white/80">Items</div>
                  <Select defaultValue={collections.find(c => c.id === activeId)?.visibility || 'public'} onValueChange={onUpdateVisibility}>
                    <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="friends">Friends</SelectItem>
                      <SelectItem value="private">Private</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 p-2">
                  {items.map(it => (
                    <div key={it.id} className="relative group border border-white/10 rounded-md overflow-hidden">
                      {it.media_type === 'image' ? (
                        <img src={`/storage/v1/object/sign/collection-media/${it.media_url}`} className="w-full h-32 object-cover" />
                      ) : (
                        <video src={`/storage/v1/object/sign/collection-media/${it.media_url}`} className="w-full h-32 object-cover" controls />
                      )}
                      <button onClick={() => onDeleteItem(it.id)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition text-xs bg-red-500/80 text-white px-2 py-1 rounded">Remove</button>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="col-span-full text-sm text-white/60 p-4">No items yet. Use "Add Item" to upload.</div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-white/60">Select a collection</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


